import {
  createContext,
  useContext,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { getCurrentWorkspaceIdSelector } from 'reducers/app';

import { getFiltersValues } from 'hocs/search/filter/utils';
import { useQueryStringParams } from 'hocs/query-string-params';
import { useFilterContext } from 'hocs/search/filter/context';

import type { Data, PlaybookTemplate } from 'components/pages/ai-insights/types';
import type { Params } from 'hocs/query-string-params/types';

type AiInsightsContextType = {
  setInsightsData: (arg0: Data) => void,
  insightsData: Data,
  playbookTemplateType: PlaybookTemplate,
  workspaceId: number | null,
  filterParams: Params,
};

const AiInsightsContext = createContext<AiInsightsContextType | undefined>(undefined);

export const AiInsightsContextProvider = ({ children }: {
  children: React.ReactNode,
}) => {
  const workspaceId = useSelector(getCurrentWorkspaceIdSelector);
  const [insightsData, setInsightsData] = useState<Data | any>({});
  const { params } = useQueryStringParams();
  const { availableFilters } = useFilterContext();
  const filterParams = getFiltersValues(params, availableFilters.current).queryParams;

  const contextValue = {
    insightsData,
    setInsightsData,
    playbookTemplateType: insightsData?.playbookTemplateType,
    workspaceId,
    filterParams,
  };

  return (
    <AiInsightsContext.Provider value={contextValue}>
      {children}
    </AiInsightsContext.Provider>
  );
};

export const useAiInsightsContext = () => {
  const context = useContext(AiInsightsContext);
  if (!context) {
    throw new Error('useAiInsightsContext must be used within an AiInsightsContextProvider');
  }
  return context;
};
